/**
 * Plan de Producción por Formato/Semana — simulador what-if.
 *
 * El usuario teclea cuánto quiere producir de cada artículo terminado por
 * semana; el motor cliente explota el BOM con netting de semielaborados y
 * recalcula la disponibilidad al instante. El backend solo entrega el feed,
 * exporta a Excel y persiste escenarios.
 */
import { useEffect, useMemo, useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import { useAppStore } from '@/store'
import { PAGE_KPIS } from '@/config/uiFlags'
import { usePlanStore } from '@/store/planStore'
import { usePlanFeed } from '@/hooks/usePlanFeed'
import { useAllowedEmpresas } from '@/hooks/useAllowedEmpresas'
import { useIsAvpOnly } from '@/hooks/useIsAvpOnly'
import { useFmtArticulo } from '@/hooks/useFmtArticulo'
import { planProduccionApi } from '@/services/api'
import { PageHeader } from '@/components/common/PageHeader'
import { RefreshButton } from '@/components/common/RefreshButton'
import { PlanGrid } from '@/components/planProduccion/PlanGrid'
import { OpportunitiesPanel } from '@/components/planProduccion/OpportunitiesPanel'
import type { Opportunity } from '@/components/planProduccion/OpportunitiesPanel'
import { ShortagesPanel } from '@/components/planProduccion/ShortagesPanel'
import type { ShortageRow } from '@/components/planProduccion/ShortagesPanel'
import { WeekDrawer } from '@/components/planProduccion/WeekDrawer'
import { CapsPanel, EMPTY_CAPS, hasCaps } from '@/components/planProduccion/CapsPanel'
import type { PlanCaps } from '@/components/planProduccion/CapsPanel'
import { useCountUp } from '@/hooks/useCountUp'
import { fmtMiles, groupByFormato, groupByFamiliaFormato, SIN_FAMILIA, keyOfArticulo } from '@/utils/planProduccion/engine'
import type { PlanMatrix } from '@/utils/planProduccion/engine'
import type { PlanExportBody, PlanExportFila } from '@/types'
import { ItemTypeFilter } from '@/components/common/ItemTypeFilter'
import type { ItemType } from '@/utils/itemType'

const DEFAULT_EMPRESA = 'avp'
// Semanas hacia delante que exploran las oportunidades
const OPP_WEEKS = 8

const nbKey = (empresa: string) => `airval-plan-nb-${empresa}`
const capsKey = (empresa: string) => `airval-plan-tope-${empresa}`

export default function PlanProduccionPage() {
  const { dark } = useAppStore()
  const { t } = useTranslation()
  const allowedEmpresas = useAllowedEmpresas()
  const isAvpOnly = useIsAvpOnly()
  const fmtArt = useFmtArticulo()
  const [empresa, setEmpresa] = useState(DEFAULT_EMPRESA)
  const [anio, setAnio] = useState(() => new Date().getFullYear())
  const [linea, setLinea] = useState('')
  const [formato, setFormato] = useState('')
  const [familiaFilter, setFamiliaFilter] = useState('')
  const [search, setSearch] = useState('')
  const [tipoArticulo, setTipoArticulo] = useState<Set<ItemType>>(new Set())
  const [notice, setNotice] = useState('')

  const avp = isAvpOnly
  const prevAvp = useRef(avp)
  useEffect(() => {
    if (prevAvp.current !== avp) { prevAvp.current = avp; setTipoArticulo(new Set()) }
  }, [avp])
  const [busy, setBusy] = useState(false)
  const [showOpps, setShowOpps] = useState(false)
  const [showShortages, setShowShortages] = useState(false)
  const [showCaps, setShowCaps] = useState(false)
  // Topes de producción semanal (global + por formato), persistidos por empresa
  const [caps, setCaps] = useState<PlanCaps>(EMPTY_CAPS)
  // Semana seleccionada → drawer derecho (materialKey = vista contextual)
  const [selectedWeek, setSelectedWeek] = useState<{ weekIdx: number; materialKey: string | null } | null>(null)
  // Densidad de la rejilla (persistida globalmente)
  const [compact, setCompact] = useState(() => {
    try { return localStorage.getItem('airval-plan-density') === 'compact' } catch { return false }
  })
  // ¿Las compras con fecha vencida cuentan como suministro?
  const [trustOverdue, setTrustOverdue] = useState(true)
  // Planificar desde 0: ignora por completo las OPs en curso (greenfield)
  const [planFromZero, setPlanFromZero] = useState(false)
  // Overrides de usuario sobre qué materiales bloquean (persistidos por empresa)
  const [nbOverrides, setNbOverrides] = useState<Record<string, boolean>>({})
  // Último artículo editado → para resaltar qué otros perdieron capacidad por él.
  const [lastEditedKey, setLastEditedKey] = useState('')

  const { feed, engine, isLoading, isError, refetch, dataUpdatedAt } = usePlanFeed(empresa, anio, linea, formato, {
    trustOverduePurchases: trustOverdue,
    planFromZero,
  })

  const {
    plan, initScope, setCell, setPlan, clearPlan,
    expandedFormatos, expandedFamilias, expandedArticulos, expandedSemis,
    toggleFormato, toggleFamilia, toggleArticulo, toggleSemi, setAllFormatos, setAllFamilias,
  } = usePlanStore()

  useEffect(() => {
    if (allowedEmpresas.length > 0 && !allowedEmpresas.includes(empresa)) {
      setEmpresa(allowedEmpresas.includes(DEFAULT_EMPRESA) ? DEFAULT_EMPRESA : allowedEmpresas[0])
    }
  }, [allowedEmpresas, empresa])

  useEffect(() => {
    if (feed) initScope(empresa, anio)
  }, [empresa, anio, feed, initScope])

  useEffect(() => {
    try {
      setNbOverrides(JSON.parse(localStorage.getItem(nbKey(empresa)) || '{}'))
    } catch {
      setNbOverrides({})
    }
    try {
      const raw = localStorage.getItem(capsKey(empresa))
      setCaps(raw ? { ...EMPTY_CAPS, ...JSON.parse(raw) } : EMPTY_CAPS)
    } catch {
      setCaps(EMPTY_CAPS)
    }
    setSelectedWeek(null)
  }, [empresa])

  // Recalcula las hojas afectadas por el último cambio (diff por referencia) y
  // aplica los overrides de bloqueo. Antes del render de la rejilla.
  useMemo(() => {
    if (!engine) return
    engine.applyBlockingOverrides(nbOverrides)
    engine.compute(plan)
  }, [engine, plan, nbOverrides])

  // ── KPIs de producción ─────────────────────────────────────────────────────
  const kpis = useMemo(() => {
    if (!engine || !feed) return { aFabricar: 0, cobertura: 100, semanasRotura: 0, enCurso: 0, demanda: 0 }
    let aFabricar = 0
    for (const row of Object.values(plan)) {
      for (const v of Object.values(row)) aFabricar += v
    }
    return {
      aFabricar,
      cobertura: engine.coveragePct(),
      semanasRotura: engine.weeksWithShortage(),
      enCurso: engine.totalBaseline(),
      demanda: engine.totalDemanda(),
    }
  }, [engine, feed, plan, nbOverrides])

  // Artículos cuya capacidad cae por culpa del último artículo editado.
  const conflicts = useMemo(
    () => (engine && lastEditedKey ? engine.conflictMap(lastEditedKey) : new Map<string, string[]>()),
    [engine, lastEditedKey, plan, nbOverrides],
  )
  const conflictCausa = useMemo(() => {
    if (!feed || !lastEditedKey || conflicts.size === 0) return ''
    return feed.articulosPlanificables.find((a) => keyOfArticulo(a) === lastEditedKey)?.descripcion ?? ''
  }, [feed, lastEditedKey, conflicts])

  const formatoByKey = useMemo(() => {
    const m = new Map<string, string>()
    for (const a of feed?.articulosPlanificables ?? []) m.set(keyOfArticulo(a), a.formato)
    return m
  }, [feed])

  /** Tracker de capacidad usada (plan + en curso) por formato/semana contra los
   *  topes configurados. Devuelve null si no hay topes activos. */
  function makeCapTracker(p: PlanMatrix) {
    if (!feed || !engine || !hasCaps(caps)) return null
    const n = feed.horizonte.length
    const byFormato = new Map<string, Float64Array>()
    const global = new Float64Array(n)
    for (const g of groupByFormato(feed.articulosPlanificables)) {
      const arr = new Float64Array(n)
      for (const a of g.articulos) {
        const k = keyOfArticulo(a)
        const row = p[k]
        for (let w = 0; w < n; w++) arr[w] += (row?.[w] ?? 0) + engine.baselineAt(k, w)
      }
      byFormato.set(g.formato, arr)
      for (let w = 0; w < n; w++) global[w] += arr[w]
    }
    const remaining = (formato: string, w: number) => {
      let rem = Infinity
      const fc = caps.formatos[formato]
      if (fc > 0) rem = Math.min(rem, fc - (byFormato.get(formato)?.[w] ?? 0))
      if ((caps.global ?? 0) > 0) rem = Math.min(rem, (caps.global as number) - global[w])
      return rem
    }
    return {
      remaining,
      exceeded: (formato: string, w: number) => remaining(formato, w) < -1e-9,
      /** Capacidad global restante en W (null si no hay tope global). */
      globalRemaining: (w: number) =>
        (caps.global ?? 0) > 0 ? (caps.global as number) - global[w] : null,
      add: (formato: string, w: number, qty: number) => {
        const arr = byFormato.get(formato)
        if (arr) arr[w] += qty
        global[w] += qty
      },
    }
  }

  const capInfo = useMemo(
    () => makeCapTracker(plan),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [feed, engine, plan, caps],
  )

  // Índice de la semana ISO actual dentro del horizonte (0 si hoy no cae en él).
  const weekNow = useMemo(() => {
    if (!feed) return 0
    const iso = new Date().toISOString().slice(0, 10)
    const i = feed.horizonte.findIndex((s) => s.fechaInicio <= iso && iso <= s.fechaFin)
    return i >= 0 ? i : 0
  }, [feed])

  // Oportunidades: artículos con capacidad de material en las próximas semanas
  // (primera semana con celda vacía y máx producible > 0).
  const opportunities = useMemo<Opportunity[]>(() => {
    if (!feed || !engine) return []
    const q = search.trim().toLowerCase()
    const arts = q
      ? feed.articulosPlanificables.filter(
          (a) =>
            a.itemid.toLowerCase().includes(q) ||
            a.descripcion.toLowerCase().includes(q) ||
            a.formato.toLowerCase().includes(q),
        )
      : feed.articulosPlanificables
    const out: Opportunity[] = []
    const wEnd = Math.min(weekNow + OPP_WEEKS, feed.horizonte.length)
    for (const a of arts) {
      const aKey = keyOfArticulo(a)
      for (let wi = weekNow; wi < wEnd; wi++) {
        if (plan[aKey]?.[wi]) continue // ya planificado esa semana
        const remCap = capInfo ? Math.floor(capInfo.remaining(a.formato, wi)) : Infinity
        if (remCap <= 0) continue // sin capacidad esa semana por el tope
        const max = engine.maxProducible(aKey, wi)
        const offer = Math.min(Number.isFinite(max) ? max : Infinity, remCap)
        if (Number.isFinite(offer) && offer > 0) {
          out.push({
            aKey,
            descripcion: a.descripcion,
            itemid: a.itemid,
            formato: a.formato,
            weekIdx: wi,
            weekLabel: feed.horizonte[wi]?.etiqueta ?? '',
            max: offer,
          })
          break
        }
      }
    }
    out.sort((x, y) => x.weekIdx - y.weekIdx || y.max - x.max)
    return out
  }, [feed, engine, plan, search, weekNow, nbOverrides, capInfo])

  // Faltantes (necesidades de compra): hojas bloqueantes en negativo.
  const shortages = useMemo<ShortageRow[]>(() => {
    if (!feed || !engine) return []
    return engine.shortageReport().map((s) => ({
      leafKey: s.leafKey,
      descripcion: s.descripcion,
      codigo: [s.itemid, s.color, s.estilo].filter(Boolean).join(' · '),
      firstWeekLabel: feed.horizonte[s.firstWeekIdx]?.etiqueta ?? '',
      maxDeficit: s.maxDeficit,
      nextInflowLabel: s.nextInflowIdx !== null ? feed.horizonte[s.nextInflowIdx]?.etiqueta ?? null : null,
    }))
  }, [feed, engine, plan, nbOverrides])

  // Resumen de la semana de referencia (seleccionada o actual) para la barra sticky
  const weekStats = useMemo(() => {
    if (!feed || !engine) return null
    const w = selectedWeek?.weekIdx ?? weekNow
    let aFabricar = 0
    for (const row of Object.values(plan)) aFabricar += row[w] ?? 0
    return {
      etiqueta: feed.horizonte[w]?.etiqueta ?? '',
      aFabricar,
      demanda: engine.demandaTotalAt(w),
      enCurso: engine.baselineTotalAt(w),
      rojos: engine.redLeavesAt(w),
      topeRestante: capInfo ? capInfo.globalRemaining(w) : null,
    }
  }, [feed, engine, plan, selectedWeek, weekNow, capInfo, nbOverrides])

  // ── Acciones ───────────────────────────────────────────────────────────────

  function handleSetCell(aKey: string, weekIdx: number, qty: number | null) {
    setLastEditedKey(aKey)
    setCell(empresa, anio, aKey, weekIdx, qty)
  }

  function handleSuggestMax(aKey: string, weekIdx: number) {
    if (!engine) return
    // El máximo se calcula SIN el valor propio de la celda (semántica de reemplazo)
    const own = plan[aKey]?.[weekIdx] ?? 0
    if (own > 0) {
      const row = { ...plan[aKey] }
      delete row[weekIdx]
      const stripped: PlanMatrix = { ...plan, [aKey]: row }
      if (Object.keys(row).length === 0) delete stripped[aKey]
      engine.compute(stripped)
    }
    const max = engine.maxProducible(aKey, weekIdx)
    // El tope semanal también acota la sugerencia (devolviendo el valor propio,
    // ya que se reemplaza)
    const formato = formatoByKey.get(aKey)
    const remCap = capInfo && formato ? Math.floor(capInfo.remaining(formato, weekIdx)) + own : Infinity
    const target = Math.min(Number.isFinite(max) ? max : Infinity, remCap)
    if (!Number.isFinite(target)) return
    setLastEditedKey(aKey)
    setCell(empresa, anio, aKey, weekIdx, Math.max(0, target))
  }

  function handleToggleBlocking(key: string) {
    setNbOverrides((prev) => {
      const base = engine?.materialByKey.get(key)?.isBlocking ?? true
      const current = prev[key] ?? base
      const newVal = !current
      const next = { ...prev }
      if (newVal === base) delete next[key] // vuelve al valor por defecto
      else next[key] = newVal
      try { localStorage.setItem(nbKey(empresa), JSON.stringify(next)) } catch { /* sin storage */ }
      return next
    })
  }

  /** Propone un plan pull: necesidad neta (demanda − en curso − plan) acotada
   *  por el máximo producible, semana a semana desde la actual. */
  function handleProposePlan() {
    if (!feed || !engine) return
    if (!window.confirm(t('planFormato.confirmProponer'))) return
    const next: PlanMatrix = { ...plan }
    const horizon = feed.horizonte.length
    const tracker = makeCapTracker(next)
    for (const a of feed.articulosPlanificables) {
      const aKey = keyOfArticulo(a)
      if (!engine.hasDemanda(aKey)) continue
      let row = next[aKey]
      for (let w = weekNow; w < horizon; w++) {
        const dem = engine.demandaAt(aKey, w)
        if (dem <= 0) continue
        const have = (row?.[w] ?? 0) + engine.baselineAt(aKey, w)
        const need = Math.ceil(dem - have)
        if (need <= 0) continue
        const remCap = tracker ? Math.floor(tracker.remaining(a.formato, w)) : Infinity
        if (remCap <= 0) continue
        engine.compute(next)
        const max = engine.maxProducible(aKey, w)
        const add = Math.min(need, Number.isFinite(max) ? max : need, remCap)
        if (add <= 0) continue
        row = { ...(row ?? {}), [w]: (row?.[w] ?? 0) + add }
        next[aKey] = row
        tracker?.add(a.formato, w, add)
      }
    }
    engine.compute(next)
    setPlan(empresa, anio, next)
    setLastEditedKey('')
  }

  function buildExportBody(): PlanExportBody | null {
    if (!feed || !engine) return null
    const n = feed.horizonte.length
    const filas: PlanExportFila[] = []

    const sumWeeks = (arts: typeof feed.articulosPlanificables) =>
      Array.from({ length: n }, (_, w) =>
        arts.reduce((acc, a) => acc + (plan[keyOfArticulo(a)]?.[w] ?? 0), 0),
      )

    for (const g of groupByFamiliaFormato(feed.articulosPlanificables)) {
      if (familiaFilter && g.familia !== familiaFilter) continue
      filas.push({
        tipo: 'formato',
        etiqueta: `FAMILIA: ${g.familia}`,
        codigo: '',
        valores: sumWeeks(g.articulos).map((v) => (v > 0 ? v : null)),
      })
      for (const fmt of g.formatos) {
        filas.push({
          tipo: 'formato',
          etiqueta: `  FORMATO: ${fmt.formato}`,
          codigo: '',
          valores: sumWeeks(fmt.articulos).map((v) => (v > 0 ? v : null)),
        })
        for (const a of fmt.articulos) {
          const aKey = keyOfArticulo(a)
          filas.push({
            tipo: 'articulo',
            etiqueta: a.descripcion,
            codigo: [fmtArt(a.itemid), a.color, a.estilo].filter(Boolean).join(' · '),
            valores: Array.from({ length: n }, (_, w) => plan[aKey]?.[w] ?? null),
          })
          const serie = (
            tipo: PlanExportFila['tipo'],
            label: string,
            at: (w: number) => number,
          ) => {
            filas.push({
              tipo,
              etiqueta: `  ↳ ${label}`,
              codigo: '',
              valores: Array.from({ length: n }, (_, w) => {
                const v = at(w)
                return v > 0 ? v : null
              }),
            })
          }
          if (engine.hasDemanda(aKey)) serie('demanda', t('planFormato.demandaRow'), (w) => engine.demandaAt(aKey, w))
          if (engine.hasBaseline(aKey)) serie('encurso', t('planFormato.enCursoRow'), (w) => engine.baselineAt(aKey, w))
          if (engine.hasRealizado(aKey)) serie('realizado', t('planFormato.realizadoRow'), (w) => engine.realizadoAt(aKey, w))
          for (const m of engine.subtreeMaterialsOf(aKey)) {
            const mat = engine.materialByKey.get(m.key)
            const [mItem, mColor, mEstilo] = m.key.split('|')
            filas.push({
              tipo: 'material',
              etiqueta: `  ${mat?.descripcion ?? fmtArt(mItem)} (Saldo)${m.isSemi ? ' [SEMI]' : ''}`,
              codigo: [fmtArt(mItem), mColor, mEstilo].filter(Boolean).join(' · '),
              valores: Array.from({ length: n }, (_, w) => engine.saldoFinAt(m.key, w)),
              rojo: Array.from({ length: n }, (_, w) => engine.isRed(m.key, w)),
            })
          }
        }
      }
    }

    const planBody = Object.entries(plan).map(([aKey, row]) => {
      const [itemid, color, estilo] = aKey.split('|')
      const cantidades: Record<string, number> = {}
      for (const [w, qty] of Object.entries(row)) {
        const sem = feed.horizonte[Number(w)]
        if (sem && qty > 0) cantidades[sem.etiqueta] = qty
      }
      return { producto_padre_id: itemid, color, estilo, cantidades }
    })

    return {
      empresa,
      anio,
      horizonte: feed.horizonte.map((s) => ({
        anio: s.anio,
        semana: s.semana,
        etiqueta: s.etiqueta,
        fecha_inicio: s.fechaInicio,
        fecha_fin: s.fechaFin,
      })),
      filas,
      plan: planBody,
    }
  }

  async function handleExport() {
    const body = buildExportBody()
    if (!body) return
    setBusy(true)
    setNotice('')
    try {
      const blob = await planProduccionApi.exportExcel(body)
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `plan_produccion_${empresa}_${anio}.xlsx`
      link.click()
      URL.revokeObjectURL(url)
    } catch {
      setNotice(t('common.error'))
    } finally {
      setBusy(false)
    }
  }

  async function handleSave() {
    const body = buildExportBody()
    if (!body) return
    setBusy(true)
    setNotice('')
    try {
      await planProduccionApi.savePlan({
        empresa,
        anio,
        nombre: `Plan ${anio}`,
        plan: body.plan,
      })
      setNotice(t('planFormato.guardadoOk'))
    } catch {
      // La entidad de Dataverse aún no existe (501) → se descarga el Excel como copia
      setNotice(t('planFormato.guardarFase2'))
      await handleExport()
    } finally {
      setBusy(false)
    }
  }

  // ── Estilos ────────────────────────────────────────────────────────────────
  const pageBg = dark ? '-m-6 p-6 bg-slate-950 text-slate-100 min-h-full' : 'text-slate-900'
  const btnBase = 'text-[11px] px-3 py-1.5 rounded-lg border transition-all font-semibold cursor-pointer disabled:opacity-50 disabled:cursor-default active:scale-[0.98]'
  const btnNeutral = dark
    ? `${btnBase} border-white/10 bg-white/5 text-slate-300 hover:bg-white/10`
    : `${btnBase} border-slate-200 bg-white text-slate-500 hover:bg-slate-50 shadow-sm`
  const btnPrimary = `${btnBase} bg-sky-500 border-sky-500 text-white hover:bg-sky-600 hover:shadow-md hover:shadow-sky-500/25`
  const inputCls = dark
    ? 'text-[11px] px-2.5 py-1.5 rounded-lg border border-white/10 bg-slate-900 text-slate-200 outline-none focus:border-sky-500 transition-colors'
    : 'text-[11px] px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white text-slate-700 outline-none focus:border-sky-400 shadow-sm transition-colors'

  const toolbarCls = dark
    ? 'border-white/8 bg-slate-900/70 backdrop-blur-md'
    : 'border-slate-200 bg-white/70 backdrop-blur-md'

  const coberturaTone =
    kpis.cobertura >= 95 ? (dark ? 'text-emerald-400' : 'text-emerald-600')
    : kpis.cobertura >= 80 ? (dark ? 'text-amber-400' : 'text-amber-600')
    : 'text-red-500'

  const formatos = useMemo(
    () => (feed ? groupByFormato(feed.articulosPlanificables).map((g) => g.formato) : []),
    [feed],
  )
  const lineas = useMemo(
    () => [...new Set(feed?.articulosPlanificables.map((a) => a.meInventline).filter(Boolean) ?? [])].sort(),
    [feed],
  )
  // Familias (nivel superior) para el filtro y todas las claves familia|formato
  // (nivel anidado) para el "expandir/colapsar todo".
  const familias = useMemo(
    () => (feed ? [...new Set(feed.articulosPlanificables.map((a) => a.meInventfamily?.trim() || SIN_FAMILIA))].sort() : []),
    [feed],
  )
  const fmtKeys = useMemo(
    () => (feed ? groupByFamiliaFormato(feed.articulosPlanificables).flatMap((g) => g.formatos.map((f) => `${g.familia}|${f.formato}`)) : []),
    [feed],
  )
  const anyExpanded = familias.some((f) => expandedFamilias[f])
  const hasDemanda = !!engine?.hasDemandaData()

  return (
    <div className={`flex flex-col gap-4 ${pageBg}`}>
      <PageHeader
        dark={dark}
        title={t('planFormato.title')}
        subtitle={
          <span className="inline-flex items-center gap-1.5 flex-wrap">
            <span className={clsx('text-[9px] font-black uppercase tracking-wide rounded-md px-1.5 py-0.5', dark ? 'bg-sky-500/15 text-sky-300' : 'bg-sky-100 text-sky-700')}>
              {empresa.toUpperCase()} · {anio}
            </span>
            {feed && (
              <span className={clsx('text-[9px] font-semibold rounded-md px-1.5 py-0.5', dark ? 'bg-white/8 text-slate-400' : 'bg-slate-100 text-slate-500')}>
                {feed.articulosPlanificables.length} {t('planFormato.articulos')} · {feed.horizonte.length} {t('planFormato.semanas')}
              </span>
            )}
            {dataUpdatedAt > 0 && (
              <span className={clsx('text-[9px] font-semibold rounded-md px-1.5 py-0.5', dark ? 'bg-emerald-500/10 text-emerald-400' : 'bg-emerald-50 text-emerald-600')}>
                {t('planFormato.actualizado', { time: new Date(dataUpdatedAt).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }) })}
              </span>
            )}
            {engine && engine.warnings.length > 0 && (
              <span
                className={clsx('text-[9px] font-semibold rounded-md px-1.5 py-0.5 cursor-help', dark ? 'bg-amber-500/15 text-amber-300' : 'bg-amber-50 text-amber-600')}
                title={engine.warnings.slice(0, 10).join('\n')}
              >
                ⚠ {engine.warnings.length} {t('planFormato.avisosBom')}
              </span>
            )}
            <span className="ml-1">{t('planFormato.subtitle')}</span>
          </span>
        }
        actions={
          <>
            {isError && <span className="text-[11px] text-red-500 font-semibold">{t('common.error')}</span>}
            {notice && <span className="text-[11px] text-sky-500 font-semibold max-w-[280px]">{notice}</span>}
            <div className={clsx('flex items-center gap-1 rounded-xl border p-1', dark ? 'border-white/10 bg-white/[0.03]' : 'border-slate-200 bg-slate-50')}>
              <select
                value={empresa}
                onChange={(e) => setEmpresa(e.target.value)}
                className={inputCls}
                title={t('common.company')}
                disabled={allowedEmpresas.length <= 1}
              >
                {(allowedEmpresas.length > 0 ? allowedEmpresas : [empresa]).map((e) => (
                  <option key={e} value={e}>{e.toUpperCase()}</option>
                ))}
              </select>
              <select
                value={anio}
                onChange={(e) => setAnio(Number(e.target.value))}
                className={inputCls}
                title={t('planFormato.anio')}
              >
                {[0, 1].map((off) => {
                  const y = new Date().getFullYear() + off
                  return <option key={y} value={y}>{y}</option>
                })}
              </select>
              <RefreshButton dark={dark} onClick={() => refetch()} />
            </div>
            <div className="flex items-center gap-1.5">
              <button onClick={handleSave} disabled={busy || !feed} className={btnNeutral}>
                {t('planFormato.guardar')}
              </button>
              <button onClick={handleExport} disabled={busy || !feed} className={btnPrimary}>
                {t('planFormato.exportar')}
              </button>
            </div>
          </>
        }
      />

      {/* ── KPIs (full width) ───────────────────────────────────────────── */}
      {PAGE_KPIS.plan && (isLoading && !feed ? (
        <div className={clsx('grid grid-cols-2 gap-3', hasDemanda ? 'lg:grid-cols-5' : 'lg:grid-cols-4')}>
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className={clsx('h-[58px] rounded-xl animate-pulse', dark ? 'bg-white/5' : 'bg-slate-100')} />
          ))}
        </div>
      ) : (
        <div className={clsx('grid grid-cols-2 gap-3', hasDemanda ? 'lg:grid-cols-5' : 'lg:grid-cols-4')}>
          <KpiCard dark={dark} icon="🏭" label={t('planFormato.kpiAFabricar')} value={kpis.aFabricar} />
          {hasDemanda && (
            <KpiCard
              dark={dark}
              icon="📦"
              label={t('planFormato.kpiDemanda')}
              value={kpis.demanda}
              tone={dark ? 'text-violet-300' : 'text-violet-600'}
            />
          )}
          <KpiCard
            dark={dark}
            icon=""
            label={t('planFormato.kpiCobertura')}
            value={kpis.cobertura}
            suffix="%"
            tone={coberturaTone}
            ring={kpis.cobertura}
          />
          <KpiCard
            dark={dark}
            icon="⚠️"
            label={t('planFormato.kpiSemanasRotura')}
            value={kpis.semanasRotura}
            tone={kpis.semanasRotura > 0 ? 'text-red-500' : undefined}
          />
          <KpiCard
            dark={dark}
            icon="⏳"
            label={t('planFormato.kpiEnCurso')}
            value={kpis.enCurso}
            tone={dark ? 'text-sky-300' : 'text-sky-600'}
          />
        </div>
      ))}

      {/* ── Panel de oportunidades (qué puedo añadir y dónde) ────────────── */}
      {showOpps && (
        <div className="plan-panel-enter">
          <OpportunitiesPanel
            dark={dark}
            items={opportunities}
            onAdd={(aKey, weekIdx, max) => handleSetCell(aKey, weekIdx, max)}
            onClose={() => setShowOpps(false)}
          />
        </div>
      )}

      {/* ── Panel de faltantes (necesidades de compra) ───────────────────── */}
      {showShortages && (
        <div className="plan-panel-enter">
          <ShortagesPanel dark={dark} items={shortages} onClose={() => setShowShortages(false)} />
        </div>
      )}

      {/* ── Panel de topes de producción semanal ─────────────────────────── */}
      {showCaps && (
        <div className="plan-panel-enter">
          <CapsPanel
            dark={dark}
            caps={caps}
            formatos={formatos}
            onChange={(next) => {
              setCaps(next)
              try { localStorage.setItem(capsKey(empresa), JSON.stringify(next)) } catch { /* sin storage */ }
            }}
            onClose={() => setShowCaps(false)}
          />
        </div>
      )}

      {/* ── Toolbar unificada + rejilla (visualmente apiladas) ───────────── */}
      <div className="flex flex-col">
        <div className={clsx('relative z-40 flex items-center gap-2 flex-wrap px-3 py-2 rounded-t-xl border', toolbarCls)}>
          <select
            value={linea}
            onChange={(e) => { setLinea(e.target.value); setFormato('') }}
            className={inputCls}
            title={t('planFormato.linea')}
          >
            <option value="">{t('planFormato.todasLineas')}</option>
            {lineas.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
          <select
            value={familiaFilter}
            onChange={(e) => setFamiliaFilter(e.target.value)}
            className={inputCls}
            title={t('planFormato.familia')}
          >
            <option value="">{t('planFormato.todasFamilias')}</option>
            {familias.map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
          <div className="w-44">
            <ItemTypeFilter dark={dark} value={tipoArticulo} onChange={setTipoArticulo} avp={avp} />
          </div>
          <select
            value={formato}
            onChange={(e) => setFormato(e.target.value)}
            className={inputCls}
            title={t('planFormato.formato')}
          >
            <option value="">{t('planFormato.todosFormatos')}</option>
            {formatos.map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('planFormato.buscar')}
            className={clsx(inputCls, 'w-56')}
          />
          <label
            className={clsx('flex items-center gap-1.5 text-[11px] font-semibold cursor-pointer select-none', dark ? 'text-slate-400' : 'text-slate-500')}
            title={t('planFormato.comprasVencidasTooltip')}
          >
            <input
              type="checkbox"
              checked={trustOverdue}
              onChange={(e) => setTrustOverdue(e.target.checked)}
              className="accent-sky-500"
            />
            {t('planFormato.comprasVencidas')}
          </label>
          <label
            className={clsx('flex items-center gap-1.5 text-[11px] font-semibold cursor-pointer select-none', planFromZero ? (dark ? 'text-amber-300' : 'text-amber-600') : dark ? 'text-slate-400' : 'text-slate-500')}
            title={t('planFormato.desdeCeroTooltip')}
          >
            <input
              type="checkbox"
              checked={planFromZero}
              onChange={(e) => setPlanFromZero(e.target.checked)}
              className="accent-amber-500"
            />
            {t('planFormato.desdeCero')}
          </label>

          <div className="flex-1" />

          <button
            onClick={() => setShowCaps((v) => !v)}
            className={
              showCaps
                ? clsx(btnBase, 'bg-amber-500 border-amber-500 text-white hover:bg-amber-600')
                : hasCaps(caps)
                  ? clsx(btnBase, dark
                      ? 'border-amber-500/40 bg-amber-500/10 text-amber-300 hover:bg-amber-500/20'
                      : 'border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100')
                  : btnNeutral
            }
            title={t('planFormato.topeTitulo')}
          >
            ⚙ {t('planFormato.tope')}
            {hasCaps(caps) && <span className="ml-1 text-[9px]">●</span>}
          </button>
          {hasDemanda && (
            <button onClick={handleProposePlan} disabled={!feed} className={clsx(
              btnBase,
              dark
                ? 'border-violet-500/40 bg-violet-500/10 text-violet-300 hover:bg-violet-500/20'
                : 'border-violet-300 bg-violet-50 text-violet-700 hover:bg-violet-100',
            )} title={t('planFormato.proponerTooltip')}>
              ✨ {t('planFormato.proponer')}
            </button>
          )}
          <button
            onClick={() => setShowShortages((v) => !v)}
            className={clsx(
              btnBase,
              showShortages
                ? 'bg-red-500 border-red-500 text-white hover:bg-red-600'
                : dark
                  ? 'border-red-500/40 bg-red-500/10 text-red-300 hover:bg-red-500/20'
                  : 'border-red-300 bg-red-50 text-red-700 hover:bg-red-100',
            )}
            title={t('planFormato.faltantesTitulo')}
          >
            🛒 {t('planFormato.faltantes')}
            {shortages.length > 0 && (
              <span className={clsx('ml-1.5 text-[9px] font-black rounded-full px-1.5 py-px', showShortages ? 'bg-white/25' : dark ? 'bg-red-500/30' : 'bg-red-200')}>
                {shortages.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setShowOpps((v) => !v)}
            className={clsx(
              btnBase,
              showOpps
                ? 'bg-emerald-500 border-emerald-500 text-white hover:bg-emerald-600'
                : dark
                  ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20'
                  : 'border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100',
            )}
            title={t('planFormato.oportunidadesTitulo')}
          >
            💡 {t('planFormato.oportunidades')}
            {opportunities.length > 0 && (
              <span className={clsx('ml-1.5 text-[9px] font-black rounded-full px-1.5 py-px', showOpps ? 'bg-white/25' : dark ? 'bg-emerald-500/30' : 'bg-emerald-200')}>
                {opportunities.length}
              </span>
            )}
          </button>
          <button
            onClick={() => {
              setCompact((v) => {
                try { localStorage.setItem('airval-plan-density', !v ? 'compact' : 'normal') } catch { /* sin storage */ }
                return !v
              })
            }}
            className={btnNeutral}
            title={t('planFormato.densidadCompacta')}
          >
            {compact ? '▤' : '▦'}
          </button>
          <button
            onClick={() => { setAllFamilias(familias, !anyExpanded); setAllFormatos(fmtKeys, !anyExpanded) }}
            className={btnNeutral}
          >
            {anyExpanded ? t('planFormato.colapsarTodo') : t('planFormato.expandirTodo')}
          </button>
          <button
            onClick={() => {
              if (window.confirm(t('planFormato.confirmVaciar'))) { clearPlan(empresa, anio); setLastEditedKey('') }
            }}
            className={btnNeutral}
          >
            {t('planFormato.vaciar')}
          </button>
        </div>

        {isLoading && (
          <div className={clsx('rounded-b-xl border border-t-0 p-4 flex flex-col gap-2', dark ? 'border-white/8 bg-slate-950' : 'border-slate-200 bg-white')}>
            <div className={clsx('text-[11px] text-center pb-1', dark ? 'text-slate-500' : 'text-slate-400')}>
              {t('planFormato.cargandoFeed')}
            </div>
            {Array.from({ length: 9 }).map((_, i) => (
              <div
                key={i}
                className={clsx('h-7 rounded-lg animate-pulse', dark ? 'bg-white/5' : 'bg-slate-100')}
                style={{ width: `${92 - (i % 4) * 14}%`, animationDelay: `${i * 70}ms` }}
              />
            ))}
          </div>
        )}
        {!isLoading && feed && engine && (
          <PlanGrid
            dark={dark}
            feed={feed}
            engine={engine}
            plan={plan}
            search={search}
            conflicts={conflicts}
            conflictCausa={conflictCausa}
            familia={familiaFilter}
            tipoArticulo={tipoArticulo}
            avp={avp}
            expandedFormatos={expandedFormatos}
            expandedFamilias={expandedFamilias}
            expandedArticulos={expandedArticulos}
            expandedSemis={expandedSemis}
            onToggleFormato={toggleFormato}
            onToggleFamilia={toggleFamilia}
            onToggleArticulo={toggleArticulo}
            onToggleSemi={toggleSemi}
            onSetCell={handleSetCell}
            onSuggestMax={handleSuggestMax}
            onToggleBlocking={handleToggleBlocking}
            selectedWeekIdx={selectedWeek?.weekIdx ?? null}
            onSelectWeek={(weekIdx, materialKey) =>
              setSelectedWeek((prev) =>
                prev && prev.weekIdx === weekIdx && prev.materialKey === (materialKey ?? null)
                  ? null
                  : { weekIdx, materialKey: materialKey ?? null },
              )
            }
            capExceeded={capInfo ? capInfo.exceeded : null}
            compact={compact}
          />
        )}

        {/* ── Barra resumen sticky (semana seleccionada o actual) ──────── */}
        {feed && engine && weekStats && (
          <div
            className={clsx(
              'sticky bottom-0 z-30 mt-2 px-4 py-2 rounded-xl border flex items-center gap-x-5 gap-y-1 flex-wrap text-[11px] backdrop-blur-md shadow-lg',
              dark ? 'border-white/10 bg-slate-900/85 text-slate-300' : 'border-slate-200 bg-white/85 text-slate-600 shadow-slate-200/60',
            )}
          >
            <span className={clsx('font-black px-2 py-0.5 rounded-lg text-[11px]', dark ? 'bg-indigo-500/20 text-indigo-300' : 'bg-indigo-100 text-indigo-700')}>
              {weekStats.etiqueta}
            </span>
            <span>
              {t('planFormato.kpiAFabricar')}: <b className="tabular-nums">{fmtMiles(weekStats.aFabricar)}</b>
            </span>
            {hasDemanda && (
              <span>
                {t('planFormato.kpiDemanda')}: <b className={clsx('tabular-nums', dark ? 'text-violet-300' : 'text-violet-600')}>{fmtMiles(weekStats.demanda)}</b>
              </span>
            )}
            {weekStats.enCurso > 0 && (
              <span>
                {t('planFormato.kpiEnCurso')}: <b className={clsx('tabular-nums', dark ? 'text-sky-300' : 'text-sky-600')}>{fmtMiles(weekStats.enCurso)}</b>
              </span>
            )}
            <span>
              {t('planFormato.materialesEnRojo')}: <b className={clsx('tabular-nums', weekStats.rojos > 0 && 'text-red-500')}>{weekStats.rojos}</b>
            </span>
            {weekStats.topeRestante !== null && (
              <span>
                {t('planFormato.sbTopeRestante')}: <b className={clsx('tabular-nums', weekStats.topeRestante < 0 ? 'text-amber-500' : dark ? 'text-emerald-400' : 'text-emerald-600')}>{fmtMiles(weekStats.topeRestante)}</b>
              </span>
            )}
          </div>
        )}
      </div>

      {/* ── Drawer de detalle de semana ──────────────────────────────────── */}
      {selectedWeek && feed && engine && feed.horizonte[selectedWeek.weekIdx] && (
        <WeekDrawer
          dark={dark}
          empresa={empresa}
          semana={feed.horizonte[selectedWeek.weekIdx]}
          weekIdx={selectedWeek.weekIdx}
          materialKey={selectedWeek.materialKey}
          engine={engine}
          feed={feed}
          onClose={() => setSelectedWeek(null)}
        />
      )}
    </div>
  )
}

// ── KPI premium: contador animado + icono / anillo de progreso ───────────────

function CoverageRing({ dark, pct }: { dark: boolean; pct: number }) {
  const r = 13
  const c = 2 * Math.PI * r
  const clamped = Math.max(0, Math.min(100, pct))
  const tone = clamped >= 95 ? '#10b981' : clamped >= 80 ? '#f59e0b' : '#ef4444'
  return (
    <svg width="34" height="34" viewBox="0 0 34 34" className="shrink-0 -rotate-90">
      <circle cx="17" cy="17" r={r} fill="none" strokeWidth="3.5" stroke={dark ? 'rgba(255,255,255,0.08)' : '#e2e8f0'} />
      <circle
        cx="17" cy="17" r={r} fill="none" strokeWidth="3.5" strokeLinecap="round"
        stroke={tone}
        strokeDasharray={c}
        strokeDashoffset={c * (1 - clamped / 100)}
        style={{ transition: 'stroke-dashoffset 0.5s cubic-bezier(0.2, 0.8, 0.3, 1), stroke 0.3s' }}
      />
    </svg>
  )
}

function KpiCard({ dark, icon, label, value, suffix, tone, ring }: {
  dark: boolean
  icon: string
  label: string
  value: number
  suffix?: string
  tone?: string
  ring?: number
}) {
  const animated = useCountUp(value)
  return (
    <div
      className={clsx(
        'group rounded-xl border px-4 py-2.5 transition-all duration-200 hover:-translate-y-0.5 cursor-default',
        dark
          ? 'border-white/8 bg-slate-900 hover:border-white/15 hover:shadow-lg hover:shadow-black/40'
          : 'border-slate-200 bg-white shadow-sm hover:shadow-md',
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className={clsx('text-[9px] font-bold uppercase tracking-wider', dark ? 'text-slate-500' : 'text-slate-400')}>
            {label}
          </div>
          <div className={clsx('text-[18px] font-black tabular-nums', tone)}>
            {fmtMiles(animated)}{suffix ?? ''}
          </div>
        </div>
        {ring !== undefined ? (
          <CoverageRing dark={dark} pct={ring} />
        ) : (
          icon && (
            <span className="text-[16px] opacity-50 group-hover:opacity-100 group-hover:scale-110 transition-all duration-200">
              {icon}
            </span>
          )
        )}
      </div>
    </div>
  )
}
